import { AlertCircle } from 'lucide-react'
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts'

import { useReport } from '@/hooks/features/reports'
import { useBusiness } from '@/contexts/BusinessContext'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

import { PageHeader } from '@/components/atoms'
import { ChartCard, ChartTooltip, chartAxis, chartGrid, compactNumber } from '@/components/dashboard/chart-kit'
import { StatCard } from '@/components/dashboard/StatCard'
import { ReportToolbar } from '@/components/reports/ReportToolbar'
import { trendOf, useReportRange } from '@/components/reports/useReportRange'

import type { OrderFunnel, SalesOverview } from '@/types/reports'

/**
 * Revenue: how gross sales become net revenue (discounts, delivery, VAT, refunds), orders per period, and what
 * happened to the online orders placed in the range (delivered, cancelled, returned, still open).
 */
const RevenueReport = () => {
  const { formatCurrency } = useBusiness()
  const { preset, range, onPresetChange, onRangeChange } = useReportRange()
  const overview = useReport<SalesOverview>('overview', { ...range })
  const funnel = useReport<OrderFunnel>('funnel', { ...range })
  const o = overview.data
  const f = funnel.data
  const note = o ? `vs ${o.previous_range.from} – ${o.previous_range.to}` : undefined

  const breakdown = o
    ? [
        ['Products sold (before discounts)', formatCurrency(o.totals.gross)],
        ['Discounts and promotions', `− ${formatCurrency(o.totals.discounts)}`],
        ['Delivery charges', `+ ${formatCurrency(o.totals.delivery)}`],
        ['VAT', `+ ${formatCurrency(o.totals.vat)}`],
        ['Refunds', `− ${formatCurrency(o.totals.refunds)}`],
      ]
    : []

  return (
    <div className="space-y-4">
      <PageHeader title="Revenue report" description="From gross sales to net revenue, and what happened to your online orders." />
      <ReportToolbar preset={preset} range={range} onPresetChange={onPresetChange} onRangeChange={onRangeChange} downloads={[{ name: 'orders', label: 'Orders (Excel)' }]} />

      {(overview.isError || funnel.isError) && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Could not load the report</AlertTitle>
          <AlertDescription>{overview.error?.message ?? funnel.error?.message}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {o && f ? (
          <>
            <StatCard title="Net revenue" value={formatCurrency(o.totals.revenue)} {...trendOf(o.changes.revenue)} note={note} />
            <StatCard title="Delivered" value={`${f.rates.delivered}%`} change={0} trend="neutral" note={`${f.delivered} of ${f.placed} online orders`} />
            <StatCard title="Cancelled" value={`${f.rates.cancelled}%`} change={0} trend="neutral" note={`${f.cancelled} orders`} />
            <StatCard title="Returned" value={`${f.rates.returned}%`} change={0} trend="neutral" note={`${f.returned} fully, ${f.partially_returned} partly`} />
          </>
        ) : (
          Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-24" />)
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-[2fr_3fr]">
        <Card>
          <CardHeader>
            <CardTitle className="w-fit text-sm font-semibold underline decoration-faint decoration-dotted underline-offset-4">How net revenue adds up</CardTitle>
          </CardHeader>
          <CardContent>
            {o ? (
              <ul className="space-y-0.5 text-sm">
                {breakdown.map(([k, v]) => (
                  <li key={k} className="flex items-center justify-between gap-3 rounded-md px-2 py-2 odd:bg-subdued">
                    <span className="text-muted-foreground">{k}</span>
                    <span className="font-medium tabular-nums text-ink">{v}</span>
                  </li>
                ))}
                <li className="flex items-center justify-between gap-3 border-t border-border-subtle px-2 pb-1 pt-2.5">
                  <span className="font-semibold text-ink">Net revenue</span>
                  <span className="font-semibold tabular-nums text-ink">{formatCurrency(o.totals.revenue)}</span>
                </li>
              </ul>
            ) : (
              <Skeleton className="h-56" />
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              Order totals already include discounts, delivery and VAT; refunds are completed returns. {o ? `${o.totals.orders} sales, ${o.totals.items} items.` : ''}
            </p>
          </CardContent>
        </Card>

        {o && (
          <ChartCard
            title="Orders over time"
            value={o.totals.orders.toLocaleString()}
            subheader={`by ${o.interval}`}
            empty={!o.series.some((s) => s.orders)}
            table={{ columns: ['Period', 'Orders'], rows: o.series.map((s) => [s.date, String(s.orders)]) }}
          >
            <BarChart data={o.series} margin={{ left: 0, right: 8 }}>
              <CartesianGrid {...chartGrid} />
              <XAxis dataKey="date" {...chartAxis} minTickGap={24} />
              <YAxis {...chartAxis} tickFormatter={compactNumber} width={40} allowDecimals={false} />
              <Tooltip content={<ChartTooltip format={(v) => Number(v).toLocaleString()} />} />
              <Bar dataKey="orders" name="Orders" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ChartCard>
        )}
      </div>

      {f && (
        <Card>
          <CardHeader>
            <CardTitle className="w-fit text-sm font-semibold underline decoration-faint decoration-dotted underline-offset-4">Online orders placed in these dates</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {[
              ['Placed', f.placed],
              ['Still open', f.open],
              ['Delivered', f.delivered],
              ['Cancelled', f.cancelled],
              ['Returned', f.returned],
            ].map(([k, v]) => (
              <div key={String(k)} className="rounded-lg bg-subdued p-3">
                <p className="text-sm text-muted-foreground">{k}</p>
                <p className="mt-1 text-lg font-semibold tabular-nums text-ink">{Number(v).toLocaleString()}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default RevenueReport
